/**
 * Hook for managing message loading and pagination
 * Handles initial message fetch, older message pagination, and prefetching
 */

import { useEffect, useState } from 'react';
import { FirestoreCacheService, MESSAGE_CACHE_LIMIT } from '@rsmaids/shared/services/api/firestoreCacheService';
import { parseWixMessage } from '../services/messageParser';
import { StaffCacheService } from '@rsmaids/shared/services/api/staffCacheService';
import { applyStaffLookup } from '../services/messageEnrichment';
import { InboxService } from '@rsmaids/shared/services/api/inboxService';
import { readThreadSnapshot, writeThreadSnapshot } from '../services/threadSnapshotStore';
// Local UI Message type (all channel types for display/receive, send restricted in components)
interface Message {
  id?: string;
  from: string;
  text: string;
  channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email';
  _createdDate: Date;
  isFromCustomer?: boolean;
  isSystemMessage?: boolean;
  avatarUrl?: string;
  extractedStaffInfo?: { staffName: string; avatarUrl?: string };
  invoicePaidData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; icon: string };
  newOrderData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; imageUrl?: string };
  media?: Array<{
    url?: string;
    type: 'image' | 'video' | 'file' | 'audio' | 'icon';
    name?: string;
    iconName?: string;
    duration?: number;
  }>;
  senderStaffName?: string;
  senderStaffAvatar?: string;
  senderType?: string;
  senderStaffId?: string;
  targetChannelIds?: string[]; // Wix channel IDs for outbound routing
}

// Module-level cache for enrichment results by sender name
// Avoids re-enriching identical messages (e.g., 15 "Helix" messages re-lookup 15 times)
const enrichmentMemoCache = new Map<string, Message>();

export interface UseMessageLoadingProps {
  realId: string;
  contactName: string;
  contactAvatarUrl?: string;
  onMessagesLoaded: (messages: Message[]) => void;
  onLoadingStateChange: (loading: boolean) => void;
  onHasMoreOlderMessagesChange: (hasMore: boolean) => void;
  seenMessageIdsRef: React.MutableRefObject<Set<string>>;
}

export const useMessageLoading = ({
  realId,
  contactName,
  contactAvatarUrl,
  onMessagesLoaded,
  onLoadingStateChange,
  onHasMoreOlderMessagesChange,
  seenMessageIdsRef,
}: UseMessageLoadingProps) => {
  // Render-visible: useMessageSubscription (a sibling hook) needs this to
  // gate its own effect, so it must be real state — a ref mutation here
  // wouldn't trigger the re-render that hook needs to see the updated value.
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  // Mirrors realId as state (not a ref) so the reset-on-change comparison
  // below is a pure render-time computation instead of a ref read/write —
  // this is React's "store info from previous render" pattern.
  const [prevRealId, setPrevRealId] = useState<string | null>(null);
  const [loadingInProgress, setLoadingInProgress] = useState(false);

  // Reset when conversation changes. Calling the setters here (during
  // render, not in an effect) is React's supported "adjust state when a prop
  // changes" pattern — it bails out this render and re-renders immediately
  // with the reset value, before any effect below runs, so
  // useMessageSubscription never sees a stale true from the previous
  // conversation.
  if (realId && realId !== prevRealId) {
    setPrevRealId(realId);
    if (loadingInProgress) {
      setLoadingInProgress(false);
    }
    if (initialLoadDone) {
      setInitialLoadDone(false);
    }
  }

  useEffect(() => {
    // Skip if no realId, already loaded, or currently loading
    if (!realId || initialLoadDone || loadingInProgress) return;

    const loadInitialMessages = async () => {
      try {
        onLoadingStateChange(true);
        const perfStart = performance.now();
        console.log(`📭 [useMessageLoading] Loading initial messages for conversation ID: ${realId}`);

        const cacheService = FirestoreCacheService.getInstance();
        const firestoreStart = performance.now();
        // The Firestore cache is always read first: it holds every message saved for the conversation (chat, emails,
        // and the rest of what Wix sends), and is read from the device's own copy when it can be. A conversation the cache
        // has NOTHING for is read from Wix, and that result is kept on the device, so reopening it skips both the (empty)
        // cache read and the Wix call; it is refreshed afterwards. The snapshot only ever stands in for an empty cache —
        // it is a copy of what the Wix API returned at some earlier moment (which leaves out business-only messages such
        // as synced emails), so it must never override a cache that has messages.
        let wixMessages = await cacheService.getCachedMessages(realId);
        const snapshot = realId.startsWith('sms-') || (wixMessages && wixMessages.length > 0)
          ? null
          : await readThreadSnapshot(realId);
        if (snapshot) wixMessages = snapshot.messages as unknown as typeof wixMessages;
        console.log(`⏱️ [PERF] ${snapshot ? 'On-device snapshot' : 'Firestore cache'} read: ${(performance.now() - firestoreStart).toFixed(0)}ms`);

        // SMS messages are pre-formatted — skip Wix parsing/enrichment entirely
        if (realId.startsWith('sms-')) {
          const smsFormatted = wixMessages as unknown as Message[];
          const sorted = [...smsFormatted].reverse() as Message[];
          onMessagesLoaded(sorted);
          sorted.forEach(m => { if (m.id) seenMessageIdsRef.current.add(m.id); });
          onHasMoreOlderMessagesChange(smsFormatted.length >= 30);
          setInitialLoadDone(true);
          return;
        }

        // Nothing cached — e.g. a conversation that is only an index entry (no messages subcollection),
        // or one the cache hasn't been filled for. Fall back to Wix directly for the newest page. The
        // visibility is 'ALL', so the business notices (email opened, new order/booking/invoice…) come
        // too, and they show in the thread like the cached ones do.
        let loadedFromWix = false;
        let wixNextCursor: string | undefined = snapshot?.nextCursor;
        if (snapshot && wixMessages && wixMessages.length > 0) loadedFromWix = true;
        if (!wixMessages || wixMessages.length === 0) {
          try {
            console.log(`🌐 [useMessageLoading] Nothing cached for ${realId} — fetching newest messages from Wix`);
            const fromWix = await InboxService.getMessagesNextPage(realId, MESSAGE_CACHE_LIMIT);
            if (fromWix.messages && fromWix.messages.length > 0) {
              wixMessages = fromWix.messages;
              wixNextCursor = fromWix.nextCursor;
              loadedFromWix = true;
              writeThreadSnapshot(realId, { messages: wixMessages as unknown[], nextCursor: wixNextCursor });
            }
          } catch (wixError) {
            console.error('[useMessageLoading] Wix fallback fetch failed:', wixError);
          }
        }

        if (wixMessages && wixMessages.length > 0) {
          console.log(`✅ [useMessageLoading] Loaded ${wixMessages.length} messages from ${loadedFromWix ? 'Wix' : 'Firestore cache'}`);

          const parseStart = performance.now();
          const uiMessages: Message[] = (wixMessages as unknown[]).map((msg) => {
            const parsed = parseWixMessage(msg, contactName || 'Contact');
            // Cast to UI Message type (restricts channel to send-enabled types)
            return parsed as unknown as Message;
          });
          console.log(`⏱️ [PERF] Message parsing: ${(performance.now() - parseStart).toFixed(0)}ms`);

          const staffCacheStart = performance.now();
          const staffCache = StaffCacheService.getInstance();
          const isLoaded = await staffCache.isStaffCacheLoaded();
          if (!isLoaded) {
            await staffCache.loadStaffCache();
          }
          console.log(`⏱️ [PERF] Staff cache load: ${(performance.now() - staffCacheStart).toFixed(0)}ms`);

          const enrichStart = performance.now();
          const enrichedMessages = uiMessages.map((msg) => {
            // Only skip if message ALREADY has complete enrichment (both staff name AND avatar)
            const hasCompleteEnrichment = msg.senderStaffName && msg.avatarUrl;
            if (hasCompleteEnrichment) {
              console.log(`⏭️  [useMessageLoading] Skipping enrichment - already complete: ${msg.from}`);
              return msg;
            }
            
            // OPTIMIZATION: Check if we already enriched a message with same sender name
            // CRITICAL: Don't return the cached message object directly - React will treat identical references as the same element
            // Instead, create a new message with the cached enrichment data
            const enrichmentKey = `${msg.senderStaffName || msg.from}`;
            if (enrichmentMemoCache.has(enrichmentKey)) {
              const cachedEnrichment = enrichmentMemoCache.get(enrichmentKey)!;
              console.log(`⚡ [useMessageLoading] Using memoized enrichment for: ${msg.from}`);
              // Create NEW message object with enrichment data from cache, but keep original message ID
              return {
                ...msg,
                from: cachedEnrichment.from,
                avatarUrl: cachedEnrichment.avatarUrl,
                senderStaffName: cachedEnrichment.senderStaffName,
                senderStaffId: cachedEnrichment.senderStaffId,
                senderType: cachedEnrichment.senderType,
              };
            }
            
            // Enrich all other messages - enrichment will handle customer vs staff determination
            console.log(`🔄 [useMessageLoading] Applying enrichment for message from: ${msg.from} (name: ${msg.senderStaffName || 'NONE'}, avatar: ${msg.avatarUrl ? 'SET' : 'NONE'})`);
            let enrichedMsg = applyStaffLookup(msg, staffCache);
            
            // For customer messages, inject contact avatar if not already set
            if (enrichedMsg.isFromCustomer && contactAvatarUrl && !enrichedMsg.avatarUrl) {
              enrichedMsg.avatarUrl = contactAvatarUrl;
              console.log(`✅ [useMessageLoading] Injected contact avatar for customer message`);
            }
            console.log(`✅ [useMessageLoading] After enrichment - from: ${enrichedMsg.from}, avatarUrl: ${enrichedMsg.avatarUrl ? 'SET' : 'UNDEFINED'}`);
            
            // Cache the enrichment result for this sender (only data, not the full message object)
            enrichmentMemoCache.set(enrichmentKey, enrichedMsg as unknown as Message);
            return enrichedMsg;
          });
          console.log(`⏱️ [PERF] Staff enrichment: ${(performance.now() - enrichStart).toFixed(0)}ms`);

          // Staff name/avatar are stamped on each message when it is written (backend), and the rest are
          // resolved from the local staff cache above — everything is on the message for the first render.
          const reversedMessages = (enrichedMessages as unknown as Message[]).reverse();
          console.log(`✅ [useMessageLoading] Processed ${reversedMessages.length} messages, calling onMessagesLoaded`);
          onMessagesLoaded(reversedMessages);

          // Track all IDs we have seen to prevent duplicates
          reversedMessages.forEach(m => {
            if (m.id) seenMessageIdsRef.current.add(m.id);
          });

          // Older messages exist only if the cache returned a full window. A short conversation came back
          // complete, so say so now: guessing 'true' hid the title card until a later server call found
          // nothing older, which re-rendered the header a second or two after the messages (the SMS path
          // below always did this correctly).
          onHasMoreOlderMessagesChange(loadedFromWix ? !!wixNextCursor : wixMessages.length >= MESSAGE_CACHE_LIMIT);
        } else {
          console.log(`📭 [useMessageLoading] No messages found for conversation ${realId}`);
          onMessagesLoaded([]);
        }

        setInitialLoadDone(true);
        if (snapshot) {
          // Shown from the device; bring it up to date from Wix without holding anything up.
          void InboxService.getMessagesNextPage(realId, MESSAGE_CACHE_LIMIT).then((fresh) => {
            if (!fresh.messages || fresh.messages.length === 0) return;
            writeThreadSnapshot(realId, { messages: fresh.messages as unknown[], nextCursor: fresh.nextCursor });
            const known = seenMessageIdsRef.current;
            const freshIds = (fresh.messages as Array<{ id?: string }>).map(m => m.id).filter(Boolean) as string[];
            if (freshIds.every(fid => known.has(fid))) return; // nothing new
            const staffCache = StaffCacheService.getInstance();
            const refreshed = (fresh.messages as unknown[]).map((msg) => {
              const parsed = parseWixMessage(msg, contactName || 'Contact') as unknown as Message;
              const enriched = applyStaffLookup(parsed, staffCache) as unknown as Message;
              if (enriched.isFromCustomer && contactAvatarUrl && !enriched.avatarUrl) enriched.avatarUrl = contactAvatarUrl;
              return enriched;
            }).reverse();
            onMessagesLoaded(refreshed);
            refreshed.forEach(m => { if (m.id) seenMessageIdsRef.current.add(m.id); });
            onHasMoreOlderMessagesChange(!!fresh.nextCursor);
          }).catch((err) => console.warn('[useMessageLoading] Background Wix refresh failed:', err));
        }
        console.log(`⏱️ [PERF] TOTAL message loading: ${(performance.now() - perfStart).toFixed(0)}ms`);
        console.log(`✅ [useMessageLoading] Initial load complete`);
      } catch (error) {
        console.error('[useMessageLoading] Error loading initial messages:', error);
        onMessagesLoaded([]);
        setLoadingInProgress(false); // Reset on error
      } finally {
        onLoadingStateChange(false);
      }
    };

    Promise.resolve().then(() => {
      // Mark loading started BEFORE async work
      setLoadingInProgress(true);
      loadInitialMessages();
    });
  }, [realId, contactName, contactAvatarUrl, onMessagesLoaded, onLoadingStateChange, onHasMoreOlderMessagesChange, seenMessageIdsRef, initialLoadDone, loadingInProgress]);

  return {
    initialLoadDone,
  };
};
